import React, { Fragment, useEffect, useRef, useState } from "react";
import axios from "../../api";
import {
  Container,
  Paper,
  Box,
  Grid,
  Typography,
  Divider,
  TextField,
  MenuItem,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
} from "@mui/material";
import { CheckCircleOutline, Lock, Devices } from "@mui/icons-material";
import SignaturePad from "react-signature-canvas";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";

function Acknowledge() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [context, setContext] = useState(null);
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);

  const [form, setForm] = useState({
    phone: "",
    address: "",
    Manager: "",
    Department: "",
    Title: "",
    City: "",
    State: "",
    Country: "",
    Zip: "",
    accessories: [],
    AccessoriesExtra: "",
    termsAccepted: false,
    idProofFile: null,
    assetImages: [],
  });
  const [signatureType, setSignatureType] = useState("upload");
  const [signatureFile, setSignatureFile] = useState(null);
  const signaturePadRef = useRef(null);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [ipAddress, setIpAddress] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Missing link token. Please use the link from your email.");
      setLoading(false);
      return;
    }

    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
      },
      (err) => console.error("Geolocation error:", err)
    );

    const ipLookupUrl =
      process.env.REACT_APP_IP_LOOKUP_URL || "https://api.ipify.org?format=json";
    fetch(ipLookupUrl)
      .then((res) => res.json())
      .then((data) => setIpAddress(data.ip))
      .catch(() => {});

    axios
      .get("/settings")
      .then((res) => setSettings(res.data.result))
      .catch(() => {});

    axios
      .get("/undertaking/ack/context", { params: { token } })
      .then((res) => setContext(res.data.result))
      .catch((err) =>
        setError(err.response?.data?.message || "This link is invalid or has expired.")
      )
      .finally(() => setLoading(false));
  }, [token]);

  const accessoriesOptions = settings?.formOptions?.accessories?.length
    ? settings.formOptions.accessories
    : ["Charger", "Bag", "Mouse", "Keyboard", "Monitor", "Headphone"];
  const termsOptions = settings?.terms?.length ? settings.terms : null;
  const appTitle = settings?.app?.appName || "Asset Undertaking";
  const companyLegalName =
    settings?.pdf?.footerName || settings?.company?.legalName || "";
  const companyAddress =
    settings?.company?.address || settings?.pdf?.footerAddress || "";

  const validateImage = (file) => {
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("PDF files are not allowed. Please upload an image.");
      return false;
    }
    if (!allowed.includes(file.type)) {
      toast.error("Only image files (JPEG, PNG, GIF, WebP) are allowed.");
      return false;
    }
    return true;
  };

  const handleFile = (e) => {
    const { name, files } = e.target;
    if (!files || !files.length) return;
    if (name === "assetImages") {
      for (const f of files) if (!validateImage(f)) return;
      setForm((p) => ({ ...p, assetImages: files }));
    } else if (name === "signatureFile") {
      if (!validateImage(files[0])) return;
      setSignatureFile(files[0]);
    } else {
      if (!validateImage(files[0])) return;
      setForm((p) => ({ ...p, [name]: files[0] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.termsAccepted) return toast.warning("Please accept the terms and conditions.");
    if (!form.idProofFile) return toast.warning("Please upload your ID proof.");

    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (key === "assetImages") {
          for (const f of val) data.append("assetImages", f);
        } else if (key === "accessories") {
          data.append("accessories", Array.isArray(val) ? val.join(", ") : val);
        } else if (key !== "idProofFile") {
          data.append(key, val);
        }
      });
      data.append("idProofFile", form.idProofFile);
      data.append("latitude", latitude);
      data.append("longitude", longitude);
      data.append("ipAddress", ipAddress);

      // Signature: either an uploaded image or a drawn canvas
      if (signatureType === "draw") {
        if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
          setSubmitting(false);
          return toast.warning("Please draw your signature.");
        }
        const url = signaturePadRef.current.toDataURL("image/png");
        const blob = await (await fetch(url)).blob();
        data.append("signatureFile", new File([blob], "signature.png", { type: "image/png" }));
      } else {
        if (!signatureFile) {
          setSubmitting(false);
          return toast.warning("Please upload your signature.");
        }
        data.append("signatureFile", signatureFile);
      }

      await axios.post("/undertaking/ack/submit", data, {
        params: { token },
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Acknowledgment submitted!");
      navigate("/thank-you");
    } catch (err) {
      toast.error(err.response?.data?.error || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmVerify = async () => {
    setConfirming(true);
    try {
      const res = await axios.post(
        "/undertaking/ack/confirm-verify",
        {},
        { params: { token } }
      );
      toast.success(res.data?.message || "Device verified.");
      navigate("/thank-you");
    } catch (err) {
      toast.error(
        err.response?.data?.error || "Could not confirm verification. Try again."
      );
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Please contact IT support for a new acknowledgment link.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Already signed once, and no open verification campaign — nothing to do.
  if (context?.alreadyAcknowledged && !context?.needsVerification) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 5, textAlign: "center" }}>
          <CheckCircleOutline color="success" sx={{ fontSize: 64, mb: 1 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
            Already acknowledged
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You have already completed the undertaking for this device. No further
            action is needed.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Prior acknowledgment exists, but a verification campaign is still Pending/Overdue.
  if (context?.canConfirmVerify) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 5, textAlign: "center" }}>
          <Devices color="primary" sx={{ fontSize: 56, mb: 1 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
            Confirm device verification
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You already signed for this device earlier. IT is running a verification
            check — confirm you still have{" "}
            <strong>
              {context.asset?.Model || context.asset?.assetId || "this device"}
            </strong>
            {context.asset?.serialNumber
              ? ` (S/N ${context.asset.serialNumber})`
              : ""}
            .
          </Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
            Status: {context.verificationStatus || "Pending"}. Click confirm to mark
            it verified for this cycle.
          </Alert>
          <Button
            variant="contained"
            size="large"
            disabled={confirming}
            onClick={handleConfirmVerify}
          >
            {confirming ? "Confirming…" : "Yes, I still have this device"}
          </Button>
          <ToastContainer position="top-center" autoClose={2800} />
        </Paper>
      </Container>
    );
  }

  const { employee, asset, stillAssigned } = context;

  return (
    <Box sx={{ minHeight: "100vh", py: { xs: 2, md: 5 } }}>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ overflow: "hidden" }}>
          {/* Header */}
          <Box
            sx={{
              p: 3,
              color: "#fff",
              background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {appTitle}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Review your assigned device and acknowledge receipt.
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 2.5, md: 4 } }}>
            {!stillAssigned && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                This device may no longer be assigned to you. If you believe this is
                an error, contact IT before submitting.
              </Alert>
            )}

            {/* Locked identity + asset details */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Devices color="primary" fontSize="small" />
              <Typography variant="h6">Device details</Typography>
              <Chip
                size="small"
                icon={<Lock sx={{ fontSize: 14 }} />}
                label="Verified from records"
                sx={{ ml: 1 }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: "#f8fafc" }}>
              <Grid container spacing={1.5}>
                {[
                  ["Employee", employee.empName || "-"],
                  ["Employee ID", employee.empId || "-"],
                  ["Email", employee.email],
                  ["Asset Tag", asset.assetId || "-"],
                  ["Serial Number", asset.serialNumber || "-"],
                  ["Model", asset.Model || "-"],
                  ["Model No", asset.ModelNo || "-"],
                  ["Manufacturer", asset.Manufacturer || "-"],
                  ["Specifications", asset.Specifications || "-"],
                  ["Category", asset.category || "-"],
                  ["Location", asset.location || "-"],
                ].map(([label, value]) => (
                  <Grid item xs={6} sm={4} key={label}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {value}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            <form onSubmit={handleSubmit}>
              {/* Contact details the employee confirms */}
              <Typography variant="h6" gutterBottom>
                Your details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  ["phone", "Phone", true],
                  ["address", "Address", true],
                  ["Department", "Department", false],
                  ["Title", "Title", false],
                  ["Manager", "Manager", false],
                  ["City", "City", false],
                  ["State", "State", false],
                  ["Country", "Country", false],
                  ["Zip", "Zip", false],
                ].map(([name, label, required]) => (
                  <Grid item xs={12} sm={6} key={name}>
                    <TextField
                      fullWidth
                      required={required}
                      label={label}
                      value={form[name]}
                      onChange={(e) => setForm((p) => ({ ...p, [name]: e.target.value }))}
                    />
                  </Grid>
                ))}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    SelectProps={{ multiple: true }}
                    label="Accessories received"
                    value={form.accessories}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        accessories:
                          typeof e.target.value === "string"
                            ? e.target.value.split(",")
                            : e.target.value,
                      }))
                    }
                  >
                    {accessoriesOptions.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Other accessories"
                    value={form.AccessoriesExtra}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, AccessoriesExtra: e.target.value }))
                    }
                  />
                </Grid>
              </Grid>

              {/* Uploads */}
              <Typography variant="h6" gutterBottom>
                Uploads
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Button variant="outlined" component="label" fullWidth>
                    {form.idProofFile ? "ID Proof ✓" : "Upload ID Proof (image)"}
                    <input hidden type="file" name="idProofFile" accept="image/*" onChange={handleFile} />
                  </Button>
                  {form.idProofFile && (
                    <Typography variant="caption" color="text.secondary">
                      {form.idProofFile.name}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button variant="outlined" component="label" fullWidth>
                    {form.assetImages?.length ? `Asset Images (${form.assetImages.length}) ✓` : "Upload Asset Images"}
                    <input hidden type="file" name="assetImages" accept="image/*" multiple onChange={handleFile} />
                  </Button>
                </Grid>
              </Grid>

              {/* Signature */}
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Signature
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <FormControl component="fieldset" sx={{ mb: 1 }}>
                <FormLabel component="legend">Signature type</FormLabel>
                <RadioGroup row value={signatureType} onChange={(e) => setSignatureType(e.target.value)}>
                  <FormControlLabel value="upload" control={<Radio />} label="Upload" />
                  <FormControlLabel value="draw" control={<Radio />} label="Draw" />
                </RadioGroup>
              </FormControl>
              {signatureType === "upload" ? (
                <Box sx={{ mb: 2 }}>
                  <Button variant="outlined" component="label">
                    {signatureFile ? "Signature ✓" : "Upload Signature (image)"}
                    <input hidden type="file" name="signatureFile" accept="image/*" onChange={handleFile} />
                  </Button>
                  {signatureFile && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {signatureFile.name}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{ border: "1px solid #cbd5e1", borderRadius: 2, width: "fit-content" }}
                    role="img"
                    aria-label="Draw your signature in this pad"
                  >
                    <SignaturePad
                      ref={signaturePadRef}
                      canvasProps={{
                        width: 400,
                        height: 160,
                        "aria-label": "Signature drawing pad",
                      }}
                    />
                  </Box>
                  <Button
                    size="small"
                    aria-label="Clear signature"
                    onClick={() => signaturePadRef.current?.clear()}
                    sx={{ mt: 1 }}
                  >
                    Clear
                  </Button>
                </Box>
              )}

              {/* Terms */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 2 }}>
                <FormControlLabel
                  sx={{ alignItems: "flex-start", ml: 0, mr: 0 }}
                  control={
                    <Checkbox
                      checked={form.termsAccepted}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, termsAccepted: e.target.checked }))
                      }
                      inputProps={{ "aria-label": "Agree to Terms and Conditions" }}
                    />
                  }
                  label="I agree to the Terms & Conditions"
                />
                <Button size="small" sx={{ mt: 1 }} onClick={() => setOpenTerms(true)}>
                  View
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Location: Lat {latitude || "--"}, Long {longitude || "--"} | IP: {ipAddress || "--"}
              </Typography>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                sx={{ mt: 2, minWidth: 160 }}
              >
                {submitting ? "Submitting…" : "Acknowledge & Submit"}
              </Button>

              {(companyLegalName || companyAddress) && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="body2" align="center" color="text.secondary">
                    <strong>{companyLegalName}</strong>
                    <br />
                    {companyAddress}
                  </Typography>
                </>
              )}
            </form>
          </Box>
        </Paper>
      </Container>

      <Dialog open={openTerms} onClose={() => setOpenTerms(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Terms and Conditions</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" component="div">
            {termsOptions ? (
              termsOptions.map((line, i) => (
                <Fragment key={i}>
                  {i + 1}. {line}
                  <br />
                </Fragment>
              ))
            ) : (
              <>
                1. This device is a company asset and should be used for official purposes.
                <br />
                2. Maintain the asset in good condition and report issues to IT.
                <br />
                3. Return the asset upon separation from the company.
                <br />
                4. You are liable for misuse, loss, or damage.
                <br />
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTerms(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-center" autoClose={2500} />
      <Backdrop open={submitting} sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}

export default Acknowledge;
